import { InboxOutlined } from "@ant-design/icons"
import type { UploadFile, UploadProps } from "antd"
import { App, Flex, Upload } from "antd"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { backupTabs, BASE_URL, STORAGE_SUF } from "../../constants"
import {
  deleteUploadedFile,
  getUploadedFiles,
} from "../../services/uploaded-files"

const suffix = localStorage.getItem(STORAGE_SUF) || ""

type Props = {
  name?: string
  fileType?: "word" | "webp" | "png"
  web?: "kn" | "ftn"
}

const { Dragger } = Upload
const { WORD_FILES, KN_IMAGES, FTN_IMAGES } = backupTabs

export const Uploader: React.FC<Props> = ({ name, fileType = "word", web }) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [fileList, setFileList] = useState<UploadFile[]>([])

  const category =
    fileType === "word" ? WORD_FILES : web === "kn" ? KN_IMAGES : FTN_IMAGES

  useEffect(() => {
    getUploadedFiles(category)
      .then((files) => {
        setFileList(
          files.map((fileName) => ({
            uid: fileName,
            name: fileName,
            status: "done",
          })),
        )
      })
      .catch(() => {
        message.error(t("uploading.fetchFilesFailedMsg"))
      })
  }, [category, t, message])

  const endpoint = fileType === "word" ? "upload" : "upload-images"

  const props: UploadProps = {
    name: "file",
    multiple: true,
    withCredentials: true,
    action: `${BASE_URL}/${endpoint}-${suffix}`,
    data: {
      web,
    },
    beforeUpload(file) {
      if (fileType === "word") {
        const isWordFile =
          file.type === "application/msword" ||
          file.type ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        if (!isWordFile) {
          message.error(
            t("uploading.wrongFileTypeMsg", { fileName: file.name }),
          )
        }
        return isWordFile || Upload.LIST_IGNORE // Prevent upload if not a Word file
      } else {
        const isWebpFile = file.type === "image/webp"
        if (!isWebpFile) {
          message.error(
            t("uploading.wrongImageFileTypeMsg", { fileName: file.name }),
          )
        }
        return isWebpFile || Upload.LIST_IGNORE // Prevent upload if not a WebP file
      }
    },
    onChange(info) {
      setFileList(info.fileList)
      const { status } = info.file
      if (status === "done") {
        message.success(
          t("uploading.uploadSuccessfulMsg", { fileName: info.file.name }),
        )
      } else if (status === "error") {
        message.error(
          t("uploading.uploadFailedMsg", { fileName: info.file.name }),
        )
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files)
    },
  }

  return (
    <Flex vertical gap="small">
      {name && <h3>{name}</h3>}
      <Dragger
        {...props}
        fileList={fileList}
        onRemove={(file) => {
          return deleteUploadedFile(category, file.name)
            .then(() => {
              message.success(
                t("uploading.deleteSuccessMsg", { fileName: file.name }),
              )
              return true
            })
            .catch(() => {
              message.error(
                t("uploading.deleteFailedMsg", { fileName: file.name }),
              )
              return false
            })
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{t("uploading.dragOrClickArea")}</p>
        {fileType === "word" && (
          <p className="ant-upload-hint">
            {t("uploading.dragOrClickAreaInfo")}
          </p>
        )}
        {fileType === "webp" && (
          <p className="ant-upload-hint">
            {t("uploading.dragOrClickAreaWebPInfo")}
          </p>
        )}
      </Dragger>
    </Flex>
  )
}
